/**
 * Background music: the tracks listed in client/public/music/tracks.json, played quietly under the
 * game. Nothing plays before the player's first click or key press (browsers forbid it), it pauses
 * while the page is hidden, it is quieter during a match, and a single track loops. With no tracks
 * listed there is no music and no music button.
 */

export interface Track {
  /** File name under /music/. An mp3 plays everywhere. */
  file: string;
  title: string;
  artist?: string;
}

/** What the player needs of an audio element: small enough for a test to fake. */
export interface AudioLike {
  src: string;
  loop: boolean;
  volume: number;
  readonly paused: boolean;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: 'ended' | 'error', cb: () => void): void;
}

export type Scene = 'menu' | 'match';

/** How loud the music is under each scene (0 to 1). A match has explosions to hear. */
export const LEVELS: Record<Scene, number> = { menu: 0.35, match: 0.2 };
const FADE_MS = 400;
const FADE_STEP_MS = 40;

export interface MusicState {
  /** Whether there is any music to play. */
  available: boolean;
  /** The track queued or playing, null when there are none. */
  track: Track | null;
  playing: boolean;
}

/** Reads tracks.json: an entry without a plain file name and a title is dropped, anything odd is ignored. */
export function parseManifest(json: unknown): Track[] {
  const list = (json as { tracks?: unknown } | null)?.tracks;
  if (!Array.isArray(list)) return [];
  const out: Track[] = [];
  for (const t of list) {
    if (!t || typeof t !== 'object') continue;
    const { file, title, artist } = t as Record<string, unknown>;
    if (typeof file !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9 ._()-]{0,119}\.(mp3|ogg|m4a|wav|opus)$/i.test(file) || file.includes('..')) continue;
    if (typeof title !== 'string' || !title.trim()) continue;
    out.push({ file, title: title.trim().slice(0, 80), artist: typeof artist === 'string' && artist.trim() ? artist.trim().slice(0, 80) : undefined });
  }
  return out.slice(0, 20);
}

export class MusicPlayer {
  tracks: Track[] = [];
  /** Told whenever what is queued or playing changes. */
  onChange: (state: MusicState) => void = () => {};

  private audio: AudioLike | null = null;
  private index = 0;
  private enabled = true;
  private scene: Scene = 'menu';
  private hidden = false;
  private unlocked = false;
  /** Tracks (by position) that would not load or play: skipped from then on. Music is over when they are all here. */
  private bad = new Set<number>();
  private failed = false;
  private fadeTimer: ReturnType<typeof setInterval> | null = null;
  private lastState = '';

  /** `makeAudio` builds the audio element (an `Audio` in the browser); `base` is the site's base path. */
  constructor(private readonly makeAudio: () => AudioLike, private readonly base = '/') {}

  get state(): MusicState {
    return { available: this.tracks.length > 0 && !this.failed, track: this.failed ? null : this.tracks[this.index] ?? null, playing: !!this.audio && !this.audio.paused };
  }

  setTracks(tracks: Track[]): void {
    this.tracks = tracks;
    this.index = 0;
    this.bad.clear();
    this.failed = false;
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
    }
    this.sync();
  }

  /** The first user gesture has happened: from here the browser lets us play. */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    this.sync();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.sync();
  }

  setScene(scene: Scene): void {
    this.scene = scene;
    this.sync();
  }

  setHidden(hidden: boolean): void {
    this.hidden = hidden;
    this.sync();
  }

  private wanted(): boolean {
    return this.unlocked && this.enabled && !this.hidden && this.tracks.length > 0 && !this.failed;
  }

  private ensureAudio(): AudioLike {
    if (this.audio) return this.audio;
    const a = this.makeAudio();
    a.volume = 0;
    a.addEventListener('ended', () => {
      // A single playable track loops on its own; with several, the next one follows.
      if (this.playable() > 1) this.next();
    });
    a.addEventListener('error', () => {
      // A missing or undecodable file: skip it, and give up quietly once none is left rather than retry forever.
      this.bad.add(this.index);
      if (this.playable() <= 0) {
        this.failed = true;
        this.sync();
      } else this.next();
    });
    this.audio = a;
    return a;
  }

  /** How many tracks are still worth playing. */
  private playable(): number {
    return this.tracks.length - this.bad.size;
  }

  private load(a: AudioLike): void {
    const t = this.tracks[this.index];
    if (!t) return;
    a.loop = this.playable() === 1;
    a.src = `${this.base}music/${encodeURIComponent(t.file)}`;
  }

  private next(): void {
    // The next track that has not failed (there is one: callers check).
    for (let i = 0; i < this.tracks.length; i++) {
      this.index = (this.index + 1) % this.tracks.length;
      if (!this.bad.has(this.index)) break;
    }
    if (this.audio) {
      this.load(this.audio);
      void this.audio.play().catch(() => {});
    }
    this.sync();
  }

  /** Brings the audio element in line with what is wanted: playing at the scene's level, or faded out and paused. */
  private sync(): void {
    if (this.wanted()) {
      const a = this.ensureAudio();
      const wantSrc = `${this.base}music/${encodeURIComponent(this.tracks[this.index]!.file)}`;
      if (!a.src || !a.src.endsWith(wantSrc.slice(this.base.length))) this.load(a);
      if (a.paused) {
        void a.play().catch((e: unknown) => {
          const name = (e as { name?: string } | null)?.name;
          // Our own pause() (muted or hidden while the file was still loading) aborts a pending play, and a file the browser
          // cannot play rejects it too (the error listener skips that track): neither is a missing gesture.
          if (name === 'AbortError' || name === 'NotSupportedError') return;
          // The browser still refused (no gesture yet after all): wait for the next unlock.
          this.unlocked = false;
        });
      }
      this.fadeTo(LEVELS[this.scene], false);
    } else if (this.audio && !this.audio.paused) {
      this.fadeTo(0, true);
    }
    this.announce();
  }

  /** A fade by the clock rather than by the tick, so a throttled timer (a page that is mostly hidden) still lands on time. */
  private fadeTo(target: number, thenPause: boolean): void {
    const a = this.audio;
    if (!a) return;
    if (this.fadeTimer) clearInterval(this.fadeTimer);
    const from = a.volume;
    const t0 = Date.now();
    this.fadeTimer = setInterval(() => {
      const k = Math.min(1, (Date.now() - t0) / FADE_MS);
      a.volume = k >= 1 ? target : from + (target - from) * k;
      if (k < 1) return;
      if (this.fadeTimer) clearInterval(this.fadeTimer);
      this.fadeTimer = null;
      if (thenPause && target === 0) {
        a.pause();
        this.announce();
      }
    }, FADE_STEP_MS);
  }

  private announce(): void {
    const s = this.state;
    const key = `${s.available}|${s.track?.file ?? ''}|${s.playing}`;
    if (key === this.lastState) return;
    this.lastState = key;
    this.onChange(s);
  }
}

/** The game's one music player. */
export const music = new MusicPlayer(() => new Audio() as unknown as AudioLike, typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL ? import.meta.env.BASE_URL : '/');

/** Fetches the track list and hands it to the player. A missing or broken list just means no music. */
export async function loadMusic(player: MusicPlayer = music, fetchFn: typeof fetch = fetch): Promise<void> {
  try {
    const res = await fetchFn(`${typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL ? import.meta.env.BASE_URL : '/'}music/tracks.json`, { cache: 'no-cache' });
    if (!res.ok) return;
    player.setTracks(parseManifest(await res.json()));
  } catch {
    /* offline, or not valid JSON: no music */
  }
}
